import React, { useEffect, useState } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, Button, Row, Col, Spinner, Alert,
} from 'reactstrap';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import { getPresignedUrl } from '../helpers/fileUpload';
import {
  createTeam, updateTeam, clearTeamMessages,
} from '../store/team/actions';
import usePermission from '../Hooks/usePermission';
import { TEAM_UPDATE } from '../constants/permissions';

const SWATCHES = ['#3B82F6', '#22C55E', '#F59E0B', '#EF4444', '#14B8A6', '#EC4899', '#8B5CF6', '#F97316'];

const TeamModal = ({ isOpen, toggle, team, onSaved }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { saving, saveError } = useSelector((state) => state.Team);

  const isEdit = Boolean(team && team.id);
  // Turning the restriction on is an access-control change, so it is administrator-only.
  const { hasPermission } = usePermission();
  const canRestrictVisibility = hasPermission(TEAM_UPDATE);
  // Only known when editing an existing team (the detail modal loads responsibilities).
  const assignedAssetTypeCount = team?.responsibilities?.assetTypes?.length ?? 0;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(SWATCHES[0]);
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadError, setUploadError] = useState('');
  const [restrictVisibility, setRestrictVisibility] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(team?.name || '');
      setDescription(team?.description || '');
      setColor(team?.color || SWATCHES[0]);
      setPhotoUrl(team?.photoUrl || '');
      setRestrictVisibility(Boolean(team?.restrictVisibility));
      setErrors({});
      setUploadError('');
      dispatch(clearTeamMessages());
    }
  }, [isOpen, team, dispatch]);

  const validate = () => {
    const next = {};
    if (!name || !name.trim()) {
      next.name = t('teams.validation.nameRequired', 'Team name is required');
    } else if (name.trim().length > 80) {
      next.name = t('teams.validation.nameTooLong', 'Team name must be 80 characters or fewer');
    }
    if (description && description.length > 500) {
      next.description = t('teams.validation.descriptionTooLong', 'Description must be 500 characters or fewer');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    setUploading(true);
    try {
      const url = await getPresignedUrl(file);
      setPhotoUrl(url);
    } catch (err) {
      console.error('Photo upload failed:', err);
      setUploadError(t('teams.photo.uploadFailed', 'Photo upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = {
      name: name.trim(),
      description: description?.trim() || '',
      color,
      photoUrl: photoUrl || null,
      restrictVisibility,
    };
    if (isEdit) {
      payload.id = team.id;
      dispatch(updateTeam(payload, (err) => { if (!err && onSaved) onSaved(); }));
    } else {
      dispatch(createTeam(payload, (err, created) => { if (!err && onSaved) onSaved(created); }));
    }
  };

  const close = () => {
    if (saving || uploading) return;
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={close} size="lg" centered>
      <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.18 }}>
        <ModalHeader toggle={close}>
          {isEdit ? t('teams.edit', 'Edit Team') : t('teams.create', 'Create Team')}
        </ModalHeader>
        <Form onSubmit={handleSubmit} noValidate>
          <ModalBody>
            {saveError && <Alert color="danger">{saveError}</Alert>}
            {uploadError && <Alert color="warning">{uploadError}</Alert>}

            <Row>
              <Col md={3} className="text-center">
                <div
                  className="team-avatar mx-auto"
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: '50%',
                    background: photoUrl ? `url(${photoUrl}) center/cover no-repeat` : color,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 32,
                    fontWeight: 600,
                    border: `3px solid ${color}`,
                    overflow: 'hidden',
                  }}
                >
                  {!photoUrl && (name?.trim()?.[0]?.toUpperCase() || 'T')}
                </div>
                <div className="mt-2">
                  <Label className="btn btn-sm btn-soft-primary mb-0">
                    <FeatherIcon icon="upload" size={14} className="me-1" />
                    {uploading
                      ? t('shared.uploading', 'Uploading...')
                      : photoUrl
                        ? t('teams.photo.replace', 'Replace photo')
                        : t('teams.photo.upload', 'Upload photo')}
                    <Input type="file" accept="image/*" hidden onChange={handleFile} disabled={uploading} />
                  </Label>
                  {photoUrl && (
                    <Button color="link" size="sm" className="text-danger px-1" onClick={() => setPhotoUrl('')} disabled={uploading}>
                      {t('shared.remove', 'Remove')}
                    </Button>
                  )}
                </div>
              </Col>
              <Col md={9}>
                <FormGroup>
                  <Label for="team-name">{t('shared.name', 'Name')} <span className="text-danger">*</span></Label>
                  <Input
                    id="team-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    invalid={!!errors.name}
                    maxLength={80}
                    placeholder={t('teams.namePlaceholder', 'e.g. Morning Shift, HVAC Crew')}
                  />
                  {errors.name && <small className="text-danger">{errors.name}</small>}
                </FormGroup>

                <FormGroup>
                  <Label for="team-description">{t('shared.description', 'Description')}</Label>
                  <Input
                    id="team-description"
                    type="textarea"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    invalid={!!errors.description}
                    maxLength={500}
                    placeholder={t('teams.descriptionPlaceholder', 'Optional — what is this team responsible for?')}
                  />
                  {errors.description && <small className="text-danger">{errors.description}</small>}
                </FormGroup>

                <FormGroup>
                  <Label>{t('teams.color.label', 'Team color')}</Label>
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    {SWATCHES.map((swatch) => {
                      const selected = (color || '').toUpperCase() === swatch.toUpperCase();
                      return (
                        <button
                          key={swatch}
                          type="button"
                          onClick={() => setColor(swatch)}
                          title={swatch}
                          aria-label={swatch}
                          aria-pressed={selected}
                          className="team-swatch"
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            background: swatch,
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            outline: 'none',
                            boxShadow: selected
                              ? `inset 0 0 0 3px #ffffff, 0 0 0 2px ${swatch}`
                              : 'none',
                            transition: 'transform 0.12s ease, box-shadow 0.12s ease',
                          }}
                        />
                      );
                    })}
                  </div>
                </FormGroup>

                {canRestrictVisibility && (
                  <FormGroup className="mt-2">
                    <div className="form-check form-switch">
                      <Input
                        type="checkbox"
                        role="switch"
                        className="form-check-input"
                        id="teamRestrictVisibility"
                        checked={restrictVisibility}
                        onChange={(e) => setRestrictVisibility(e.target.checked)}
                      />
                      <Label className="form-check-label" for="teamRestrictVisibility">
                        {t('teams.restrictVisibility.label', "Restrict members' visibility")}
                      </Label>
                    </div>
                    <small className="text-muted d-block mt-1">
                      {t(
                        'teams.restrictVisibility.help',
                        'Members only see assets whose type is assigned to this team, and the work orders linked to them.',
                      )}
                    </small>
                    {restrictVisibility && isEdit && assignedAssetTypeCount === 0 && (
                      <Alert color="warning" className="mt-2 mb-0 py-2 px-3">
                        <small>
                          {t(
                            'teams.restrictVisibility.noAssetTypesWarning',
                            'No asset types are assigned yet — members of this team will see no assets until you assign at least one.',
                          )}
                        </small>
                      </Alert>
                    )}
                  </FormGroup>
                )}
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline type="button" onClick={close} disabled={saving || uploading}>
              {t('shared.cancel', 'Cancel')}
            </Button>
            <Button color="primary" type="submit" disabled={saving || uploading}>
              {saving ? <><Spinner size="sm" className="me-2" />{t('shared.saving', 'Saving...')}</> : (isEdit ? t('shared.save', 'Save') : t('shared.create', 'Create'))}
            </Button>
          </ModalFooter>
        </Form>
        <style>{`
          .team-swatch:hover { transform: scale(1.08); }
          .team-swatch:focus-visible { box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.35); }
        `}</style>
      </motion.div>
    </Modal>
  );
};

export default TeamModal;
